/**
 * Plain-English renderer for rule conditions and actions.
 *
 * Converts the JSON shapes stored in rules.conditions / rules.actions into
 * React nodes a non-programmer can read. Backbone of the demo: every rule
 * the system uses is inspectable here without reading code or schema.
 *
 * Defensive by design — if a condition or action carries an operator/verb
 * the renderer does not know, it renders a clearly-labelled fallback
 * showing the raw JSON instead of throwing.
 */

import * as React from 'react';

// ---------------------------------------------------------------------------
// Humanization helpers
// ---------------------------------------------------------------------------

/** Snake_case → Title Case ("water_category" → "Water Category"). */
export function humanize(raw: string): string {
  if (!raw) return '';
  return raw
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Awkward auto-humanizations we override by hand. Add sparingly — the
 * register exists so the demo never reads "Has Multi Story" or
 * "Is Commercial" verbatim. Keys are matched after prefix stripping.
 */
const FACT_LABEL_OVERRIDES: Record<string, string> = {
  has_multi_story: 'Multi-story property',
  is_commercial: 'Commercial property',
  scope_item_count: 'Scope item count',
  total_area_sf: 'Total area (SF)',
  water_category: 'Water category',
  damage_type: 'Damage type',
  is_flood: 'Flood claim',
  is_interior_room: 'Interior room',
  has_overhead_work: 'Overhead work present',
  has_paint: 'Paint work present',
  has_drywall: 'Drywall work present',
  has_drywall_installed: 'New drywall installed',
  has_ceiling_item: 'Ceiling line item present',
  needs_paint: 'Paint required',
  needs_texture: 'Texture required',
  needs_masking: 'Masking required',
};

/**
 * Stored fact references appear in three shapes:
 *   {fact: "x"}            → "x"
 *   {field: "facts.x"}     → "x"
 *   {field: "item.x"}      → "the current item's x"
 * Returns a humanized label + a flag indicating per-item context so the
 * caller can phrase the sentence appropriately.
 */
export function factLabel(name: string): { label: string; perItem: boolean } {
  if (!name) return { label: '', perItem: false };
  const perItem = name.startsWith('item.');
  const stripped = name.replace(/^item\./, '').replace(/^facts\./, '');
  const override = FACT_LABEL_OVERRIDES[stripped];
  const label = override || humanize(stripped);
  return { label, perItem };
}

// ---------------------------------------------------------------------------
// Value formatting + enum labels
// ---------------------------------------------------------------------------

/** Known enum-valued facts where a numeric/code value has a friendly label. */
const ENUM_LABELS: Record<string, Record<string, string>> = {
  water_category: { '1': 'Clean', '2': 'Gray', '3': 'Black' },
};

const ACTIVITY_CODE_LABELS: Record<string, string> = {
  '+': 'Install only',
  '-': 'Remove only',
  '&': 'Remove & Replace',
  '+&': 'Remove & Replace',
};

function enumSuffix(factName: string, value: unknown): string {
  const stripped = factName.replace(/^item\./, '').replace(/^facts\./, '');
  const map = ENUM_LABELS[stripped];
  if (!map) return '';
  const key = String(value);
  return map[key] ? ` (${map[key]})` : '';
}

/** Render a leaf value: booleans, numbers, strings, arrays. */
function ValueChip({ value }: { value: unknown }): React.ReactElement {
  if (value === true) return <em>true</em>;
  if (value === false) return <em>false</em>;
  if (value === null || value === undefined) return <em>null</em>;
  if (Array.isArray(value)) {
    return (
      <span>
        {value.map((v, i) => (
          <React.Fragment key={i}>
            {i > 0 && ', '}
            <ValueChip value={v} />
          </React.Fragment>
        ))}
      </span>
    );
  }
  if (typeof value === 'object') {
    return <code className="text-xs">{JSON.stringify(value)}</code>;
  }
  // Strings and numbers — short codes (≤6 chars all caps/symbols) get
  // monospaced styling so "WTR REM" reads as a code rather than a phrase.
  const s = String(value);
  const looksLikeCode = /^[A-Z0-9_+&\-/. ]{1,12}$/.test(s) && /[A-Z]/.test(s);
  if (looksLikeCode) return <code className="rounded bg-muted px-1 text-xs">{s}</code>;
  return <span>{typeof value === 'number' ? s : `'${s}'`}</span>;
}

// ---------------------------------------------------------------------------
// Condition renderer
// ---------------------------------------------------------------------------

/** Operator aliases — accept both stored ("eq") and spec ("equals") names. */
const COMPARISON_PHRASING: Record<string, (_left: React.ReactNode, _right: React.ReactNode) => React.ReactNode> = {
  eq: (l, r) => <>{l} equals {r}</>,
  equals: (l, r) => <>{l} equals {r}</>,
  neq: (l, r) => <>{l} is not {r}</>,
  not_equals: (l, r) => <>{l} is not {r}</>,
  gt: (l, r) => <>{l} is greater than {r}</>,
  gte: (l, r) => <>{l} is at least {r}</>,
  lt: (l, r) => <>{l} is less than {r}</>,
  lte: (l, r) => <>{l} is at most {r}</>,
  in: (l, r) => <>{l} is one of: {r}</>,
  not_in: (l, r) => <>{l} is none of: {r}</>,
  contains: (l, r) => <>{l} contains {r}</>,
};

const UNARY_PHRASING: Record<string, (_left: React.ReactNode) => React.ReactNode> = {
  exists: (l) => <>{l} is set</>,
  not_exists: (l) => <>{l} is not set</>,
};

function FactRef({ name, perItem }: { name: string; perItem: boolean }): React.ReactElement {
  if (perItem) {
    return (
      <>
        the current item's <strong>{name}</strong>
      </>
    );
  }
  return <strong>{name}</strong>;
}

function UnrecognizedFallback({ kind, data }: { kind: string; data: unknown }): React.ReactElement {
  return (
    <span className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-900">
      Unrecognized {kind}: <code className="text-[11px]">{JSON.stringify(data)}</code>
    </span>
  );
}

function renderLeafCondition(condition: Record<string, unknown>): React.ReactNode {
  // Per-item special: {item_field_empty: "<field>"}
  if ('item_field_empty' in condition) {
    const field = condition.item_field_empty;
    return (
      <>
        the current item's <strong>{humanize(String(field))}</strong> field is empty
      </>
    );
  }

  // Per-item special: {scope_contains: {category, activity_in, min_count, ...}}
  if ('scope_contains' in condition) {
    const c = (condition.scope_contains || {}) as Record<string, unknown>;
    const parts: React.ReactNode[] = [];
    const min = (c.min_count as number | undefined) ?? 1;
    if (c.category) parts.push(<>Category is <ValueChip value={c.category} /></>);
    if (c.activity_in) {
      const list = (c.activity_in as unknown[]).map((a) => ACTIVITY_CODE_LABELS[String(a)] || String(a));
      parts.push(<>Activity is one of: <ValueChip value={list} /></>);
    }
    if (c.surface) parts.push(<>Surface is <ValueChip value={c.surface} /></>);
    if (c.room) parts.push(<>Room is <ValueChip value={c.room} /></>);
    return (
      <>
        the scope contains at least {min} item{min === 1 ? '' : 's'} where{' '}
        {parts.length === 0
          ? <em>(no criteria)</em>
          : parts.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && ' and '}
                {p}
              </React.Fragment>
            ))}
      </>
    );
  }

  // Standard: {fact: "x", op: "eq", value: ...} OR {field: "...", op: ..., value: ...}
  const factName = (condition.fact ?? condition.field) as string | undefined;
  const op = condition.op as string | undefined;

  if (!factName || !op) {
    return <UnrecognizedFallback kind="condition" data={condition} />;
  }

  const { label, perItem } = factLabel(factName);
  const left = <FactRef name={label} perItem={perItem} />;

  if (op in UNARY_PHRASING) {
    return UNARY_PHRASING[op](left);
  }

  if (op in COMPARISON_PHRASING) {
    const rawValue = condition.value;
    const right = (
      <>
        <ValueChip value={rawValue} />
        {enumSuffix(factName, rawValue)}
      </>
    );
    return COMPARISON_PHRASING[op](left, right);
  }

  return <UnrecognizedFallback kind="condition" data={condition} />;
}

const GROUP_LABELS: Record<string, string> = {
  all: 'All of the following:',
  any: 'Any of the following:',
  none: 'None of the following:',
};

/** Render a single condition node — group ({all|any|none}) or leaf. */
export function ConditionNode({
  condition,
  depth = 0,
}: {
  condition: Record<string, unknown>;
  depth?: number;
}): React.ReactElement {
  for (const groupKey of ['all', 'any', 'none'] as const) {
    if (Array.isArray(condition[groupKey])) {
      const children = condition[groupKey] as Array<Record<string, unknown>>;
      if (children.length === 0) {
        return <span className="text-muted-foreground italic">(empty {groupKey} group)</span>;
      }
      return (
        <div>
          <div className="font-medium text-sm">{GROUP_LABELS[groupKey]}</div>
          <ul
            className={
              'ml-3 mt-1 space-y-1 border-l-2 pl-3 ' +
              (groupKey === 'any'
                ? 'border-blue-200'
                : groupKey === 'none'
                ? 'border-red-200'
                : 'border-slate-200')
            }
          >
            {children.map((child, i) => (
              <li key={i} className="text-sm">
                <ConditionNode condition={child} depth={depth + 1} />
              </li>
            ))}
          </ul>
        </div>
      );
    }
  }
  return <>{renderLeafCondition(condition)}</>;
}

/**
 * Top-level entry: render the entire conditions dict for a rule.
 * The shape is {all?: [...], any?: [...], none?: [...]} — render each
 * present group as a section. Empty dict means "always fires".
 */
export function RuleConditions({
  conditions,
}: {
  conditions: Record<string, unknown> | null | undefined;
}): React.ReactElement {
  if (!conditions || Object.keys(conditions).length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        No conditions — this rule applies in every evaluation.
      </p>
    );
  }
  const sections: React.ReactNode[] = [];
  for (const key of ['all', 'any', 'none'] as const) {
    const list = conditions[key];
    if (Array.isArray(list) && list.length > 0) {
      sections.push(<ConditionNode key={key} condition={{ [key]: list }} />);
    }
  }
  if (sections.length === 0) {
    return <UnrecognizedFallback kind="condition root" data={conditions} />;
  }
  return <div className="space-y-3">{sections}</div>;
}

// ---------------------------------------------------------------------------
// Action renderer
// ---------------------------------------------------------------------------

function activityLabel(code: unknown): React.ReactNode {
  if (code === null || code === undefined) return <em>none</em>;
  const key = String(code);
  const label = ACTIVITY_CODE_LABELS[key];
  if (label) {
    return (
      <>
        <code className="rounded bg-muted px-1 text-xs">{key}</code>{' '}
        <span className="text-muted-foreground">({label})</span>
      </>
    );
  }
  return <code className="rounded bg-muted px-1 text-xs">{key}</code>;
}

function renderQuantityValue(value: Record<string, unknown> | undefined): React.ReactNode {
  if (!value) return <em>unspecified</em>;
  const type = value.type as string | undefined;
  const unit = (value.unit as string | undefined) ?? '';
  if (type === 'literal') {
    const v = value.value;
    return (
      <>
        <ValueChip value={v} />
        {unit && <> {unit}</>}
      </>
    );
  }
  // Default to range
  const { min, max, default: def } = value as Record<string, unknown>;
  if (min == null && max == null && def == null) {
    return <>a contractor-specified amount{unit && <> in {unit}</>}</>;
  }
  const range = min != null && max != null ? `${min}–${max}` : min != null ? `≥${min}` : `≤${max}`;
  return (
    <>
      {range}
      {unit && <> {unit}</>}
      {def != null && <> (default {String(def)})</>}
    </>
  );
}

function renderTarget(target: Record<string, unknown> | undefined): React.ReactNode {
  if (!target || Object.keys(target).length === 0) return <em>all matching items</em>;
  const parts: React.ReactNode[] = [];
  for (const [k, v] of Object.entries(target)) {
    parts.push(
      <span key={k}>
        <strong>{humanize(k)}</strong>={' '}
        <ValueChip value={v} />
      </span>,
    );
  }
  return parts.map((p, i) => (
    <React.Fragment key={i}>
      {i > 0 && ' and '}
      {p}
    </React.Fragment>
  ));
}

/** Render a single action. Public entry point. */
export function ActionNode({ action }: { action: Record<string, unknown> }): React.ReactElement {
  const type = action.type as string | undefined;

  switch (type) {
    case 'add_scope_item': {
      const code = (action.xactimate_code ?? action.code) as string | undefined;
      const desc = action.description as string | undefined;
      const activity = action.activity as string | undefined;
      const unit = action.unit as string | undefined;
      return (
        <>
          Add scope item{' '}
          {code ? <code className="rounded bg-muted px-1 text-xs">{code}</code> : <em>(no code)</em>}
          {desc && <> — {desc}</>}
          {activity && <> ({activity}{unit && <>, {unit}</>})</>}
        </>
      );
    }
    case 'add_flag': {
      const sev = (action.severity as string) || 'info';
      const msg = (action.message ?? action.message_template) as string | undefined;
      return (
        <>
          Add a <strong>{sev}</strong> flag{msg && <>: "{msg}"</>}
        </>
      );
    }
    case 'add_suggestion': {
      const msg = action.message as string | undefined;
      const conf = action.confidence as number | undefined;
      return (
        <>
          Add a suggestion{msg && <>: "{msg}"</>}
          {conf != null && (
            <span className="ml-1 text-xs text-muted-foreground">
              (confidence {(conf * 100).toFixed(0)}%)
            </span>
          )}
        </>
      );
    }
    case 'add_fact': {
      const key = action.fact_key as string | undefined;
      const value = action.fact_value;
      if (!key) return <UnrecognizedFallback kind="action" data={action} />;
      return (
        <>
          Set fact <strong>{humanize(key)}</strong> to <ValueChip value={value} />
        </>
      );
    }
    case 'remove_item': {
      const pattern = action.pattern as Record<string, unknown> | undefined;
      return (
        <>
          Remove items matching: {renderTarget(pattern)}
        </>
      );
    }
    case 'modify_activity': {
      const newAct = action.new_activity;
      const rationale = action.rationale_template as string | undefined;
      return (
        <>
          Change the current item's activity to {activityLabel(newAct)}
          {rationale && (
            <>
              {' '}
              <span className="text-muted-foreground">
                with rationale: "{rationale}"
              </span>
            </>
          )}
        </>
      );
    }
    case 'substitute_activity': {
      const policy = (action.selection_policy as string) || 'first_supported';
      const source = (action.lookup_source as string) || 'canonical_xactimate_codes';
      const policyLabel =
        policy === 'first_supported'
          ? 'the first supported activity'
          : `the activity selected by policy "${policy}"`;
      return (
        <>
          Replace the current item's activity with {policyLabel} from the{' '}
          <span className="font-mono text-xs">{source}</span> lookup
        </>
      );
    }
    case 'set_quantity': {
      const target = action.target as Record<string, unknown> | undefined;
      const value = action.value as Record<string, unknown> | undefined;
      return (
        <>
          Set quantity for {renderTarget(target)} to {renderQuantityValue(value)}
        </>
      );
    }
    case 'validate_activity_in_supported_set': {
      const sev = (action.severity as string) || 'warning';
      const tmpl = action.flag_message_template as string | undefined;
      return (
        <>
          Verify the current item's activity is in the supported set for its code family;
          if not, add a <strong>{sev}</strong> flag
          {tmpl && <>: "{tmpl}"</>}
        </>
      );
    }
    default:
      return <UnrecognizedFallback kind="action" data={action} />;
  }
}

/** Render an ordered list of actions. */
export function RuleActions({
  actions,
}: {
  actions: Array<Record<string, unknown>> | null | undefined;
}): React.ReactElement {
  if (!actions || actions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">No actions.</p>
    );
  }
  return (
    <ol className="ml-4 list-decimal space-y-2 text-sm">
      {actions.map((a, i) => (
        <li key={i}>
          <ActionNode action={a} />
        </li>
      ))}
    </ol>
  );
}
